import React, { useEffect, useRef, useState } from 'react';
import { MarkdownPreview } from './MarkdownPreview';

interface MarkdownEditorProps {
  content: string;
  onChange: (content: string) => void;
  autoFocus?: boolean;
}

type ViewMode = 'write' | 'preview' | 'split';

export function MarkdownEditor({ content, onChange, autoFocus = false }: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [autoFocus]);

  // Auto-resize textarea in write-only mode
  useEffect(() => {
    if (viewMode === 'write' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [content, viewMode]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

    // Keyboard shortcuts
    if (cmdOrCtrl) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertMarkdown('**', '**');
          return;
        case 'i':
          e.preventDefault();
          insertMarkdown('*', '*');
          return;
        case 'k':
          e.preventDefault();
          insertMarkdown('[', '](url)');
          return;
        case 'e':
          e.preventDefault();
          insertMarkdown('`', '`');
          return;
      }
    }

    // Handle tab for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newValue = content.substring(0, start) + '  ' + content.substring(end);
      onChange(newValue);

      // Move cursor after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  const insertMarkdown = (before: string, after: string = '') => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    const newValue =
      content.substring(0, start) +
      before +
      selectedText +
      after +
      content.substring(end);

    onChange(newValue);

    // Set cursor position after insertion
    setTimeout(() => {
      const newPosition = start + before.length + selectedText.length;
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = newPosition;
    }, 0);
  };

  const insertList = (prefix: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    if (selectedText.includes('\n')) {
      // Multi-line: add prefix to each line
      const lines = selectedText.split('\n');
      const newText = lines.map(line => line ? `${prefix} ${line}` : line).join('\n');
      const newValue = content.substring(0, start) + newText + content.substring(end);
      onChange(newValue);
    } else {
      // Single line: just add prefix
      const newValue = content.substring(0, start) + `${prefix} ${selectedText}` + content.substring(end);
      onChange(newValue);
    }

    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  const insertHeading = (level: number) => {
    const hashes = '#'.repeat(level);
    insertMarkdown(`${hashes} `, '');
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="border border-gray-200 rounded-xl bg-white shadow-sm">
        {/* View mode tabs */}
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 bg-gray-50 rounded-t-xl">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setViewMode('write')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'write'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Write
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'preview'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                viewMode === 'split'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Split View
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowShortcuts(!showShortcuts)}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
              title="Keyboard shortcuts"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Shortcuts
            </button>
            <span className="text-xs text-gray-500">
              {content.length} characters
            </span>
          </div>
        </div>

        {/* Formatting toolbar */}
        {viewMode !== 'preview' && (
          <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-gray-200">
            {/* Headings */}
            <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200">
              {[1, 2, 3].map((level) => (
                <button
                  key={level}
                  onClick={() => insertHeading(level)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title={`Heading ${level}`}
                >
                  <span className="text-sm font-bold">H{level}</span>
                </button>
              ))}
            </div>

            {/* Text formatting */}
            <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200">
              <button
                onClick={() => insertMarkdown('**', '**')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Bold (Cmd+B)"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h8.5a4.5 4.5 0 013 7.89A5 5 0 0116 21H6V4zm8 6.5a2 2 0 100-4H9v4h5zm-5 2v5h6a2.5 2.5 0 000-5H9z"/>
                </svg>
              </button>
              <button
                onClick={() => insertMarkdown('*', '*')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Italic (Cmd+I)"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 5h8v3h-2.5l-2.5 8H16v3H8v-3h2.5l2.5-8H10V5z"/>
                </svg>
              </button>
              <button
                onClick={() => insertMarkdown('`', '`')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-mono text-sm"
                title="Code"
              >
                {'<>'}
              </button>
              <button
                onClick={() => insertMarkdown('~~', '~~')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Strikethrough"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M9 5l-1 14M16 5l1 14" />
                </svg>
              </button>
            </div>

            {/* Lists and quotes */}
            <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200">
              <button
                onClick={() => insertList('-')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Bullet list"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => insertList('1.')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Numbered list"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 10h18M3 16h18M3 4v6M3 10v6" />
                </svg>
              </button>
              <button
                onClick={() => insertMarkdown('> ', '')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Quote"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
                </svg>
              </button>
            </div>

            {/* Links and code blocks */}
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => insertMarkdown('[', '](url)')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Link"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </button>
              <button
                onClick={() => insertMarkdown('```\n', '\n```')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Code block"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Keyboard shortcuts panel */}
        {showShortcuts && (
          <div className="border-b border-gray-200 bg-blue-50 px-4 py-3">
            <div className="text-sm space-y-2">
              <div className="font-semibold text-gray-900 mb-3">Keyboard Shortcuts</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Bold</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm font-mono">⌘/Ctrl + B</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Italic</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm font-mono">⌘/Ctrl + I</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Link</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm font-mono">⌘/Ctrl + K</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Code</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm font-mono">⌘/Ctrl + E</kbd>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Indent</span>
                  <kbd className="px-2 py-1 bg-white border border-gray-300 rounded shadow-sm font-mono">Tab</kbd>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content area */}
        <div className={`${viewMode === 'split' ? 'grid grid-cols-2 divide-x divide-gray-200' : ''}`}>
          {/* Editor */}
          {viewMode !== 'preview' && (
            <div className={viewMode === 'split' ? 'overflow-hidden' : ''}>
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`w-full p-4 font-mono text-sm border-0 focus:ring-0 outline-none resize-none ${
                  viewMode === 'split' ? 'h-[500px]' : 'min-h-[400px]'
                }`}
                placeholder="Write your markdown content here..."
              />
            </div>
          )}

          {/* Preview */}
          {viewMode !== 'write' && (
            <div className={`${viewMode === 'split' ? 'overflow-y-auto h-[500px] p-4' : 'p-4 min-h-[400px]'} bg-gray-50`}>
              <MarkdownPreview content={content} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
